export type ShowcaseTabId = 'individual' | 'family' | 'social' | 'trust' | 'institution';

export interface ShowcaseFeatureCallout {
  label: string;
  description: string;
}

export interface ShowcaseTabConfig {
  id: ShowcaseTabId;
  navLabel: string;
  heading: string;
  copy: string;
  ctaLabel: string;
  ctaHref: string;
  frameLabel: string;
  features: ShowcaseFeatureCallout[];
  accent: string;
  accentSoft: string;
  accentBorder: string;
}

export const SHOWCASE_TABS: ShowcaseTabConfig[] = [
  {
    id: 'individual',
    navLabel: 'Individual',
    heading: 'A living record of your life',
    copy: 'Bring the moments, people, places and achievements that shaped your life into one evolving record.',
    ctaLabel: 'Begin your living record',
    ctaHref: '/register',
    frameLabel: 'Individual · Living Record Overview',
    features: [
      {
        label: 'Preserved memories',
        description:
          'Photos, stories and reflections kept together instead of scattered across apps.',
      },
      {
        label: 'Life chapters',
        description: 'Childhood, school, career and legacy, organized by era as your record grows.',
      },
      {
        label: 'Journey progress',
        description: 'A visual map of milestones plotted across your life, not a follower count.',
      },
      {
        label: 'Meaningful next action',
        description:
          'Real, data-driven suggestions drawn from your record -- never fabricated streaks.',
      },
    ],
    accent: '#1d9e75',
    accentSoft: 'rgba(29,158,117,0.12)',
    accentBorder: 'rgba(29,158,117,0.32)',
  },
  {
    id: 'family',
    navLabel: 'Family',
    heading: 'Preserve the people, not just the paperwork',
    copy: 'Preserve more than names and dates. Keep the stories, voices and connections between generations.',
    ctaLabel: 'Explore family preservation',
    ctaHref: '/preservation-ledger',
    frameLabel: 'Family · Family Ledger',
    features: [
      {
        label: 'Real relationships',
        description: 'A family tree built from relationships, not a flat contact list.',
      },
      {
        label: 'Photographs with context',
        description: 'Each photo keeps who, where, and the story behind it.',
      },
      {
        label: 'Recollection status',
        description:
          'See what is a draft memory, family-confirmed, or evidence-backed at a glance.',
      },
    ],
    accent: '#e88a73',
    accentSoft: 'rgba(232,138,115,0.14)',
    accentBorder: 'rgba(232,138,115,0.34)',
  },
  {
    id: 'social',
    navLabel: 'Social',
    heading: 'Stories that become part of something lasting',
    copy: 'Share stories that can become part of a larger living record instead of disappearing into a temporary feed.',
    ctaLabel: 'Explore public stories',
    ctaHref: '/community',
    frameLabel: 'Social · Story',
    features: [
      {
        label: 'Meaningful reactions',
        description: 'Witnessed and discussed, not just liked and forgotten by tomorrow.',
      },
      {
        label: 'Connected context',
        description: 'Every public story can link to the people, places and journey behind it.',
      },
      {
        label: 'Your story, your record',
        description: 'What you share publicly can also strengthen your own living record.',
      },
    ],
    accent: '#5fa8d3',
    accentSoft: 'rgba(95,168,211,0.13)',
    accentBorder: 'rgba(95,168,211,0.32)',
  },
  {
    id: 'trust',
    navLabel: 'Trust',
    heading: 'Credibility when credibility matters',
    copy: 'Add credibility when credibility matters through evidence, witnesses, review, validation and institutional confirmation.',
    ctaLabel: 'Explore the Trust Layer',
    ctaHref: '/trust-ledger',
    frameLabel: 'Trust · Credential record',
    features: [
      {
        label: 'Evidence attached',
        description: 'Documents and media that support the record, kept alongside it.',
      },
      {
        label: 'Witness confirmation',
        description: 'People who can vouch for what happened, on record.',
      },
      {
        label: 'Institutional confirmation',
        description: 'A trusted organization can review and confirm a credential or achievement.',
      },
      {
        label: 'Precise record status',
        description: 'Draft, evidence-attached, or institutionally confirmed -- always visible.',
      },
    ],
    accent: '#a9d9f2',
    accentSoft: 'rgba(95,168,211,0.14)',
    accentBorder: 'rgba(95,168,211,0.34)',
  },
  {
    id: 'institution',
    navLabel: 'Institution',
    heading: 'Institutional memory, built to last',
    copy: 'Recognize participation, validate achievement, preserve institutional memory and maintain trusted records.',
    ctaLabel: 'Explore institution solutions',
    ctaHref: '/for-institutions',
    frameLabel: 'Institution · Overview',
    features: [
      {
        label: 'Programs and participants',
        description: 'Track programs, cohorts and the people moving through them.',
      },
      {
        label: 'Review and issuance',
        description:
          'Review submissions and issue confirmed records without leaving the workspace.',
      },
      {
        label: 'Heritage Archive',
        description: 'Preserve institutional history alongside the records you issue today.',
      },
    ],
    accent: '#9a7bb8',
    accentSoft: 'rgba(154,123,184,0.14)',
    accentBorder: 'rgba(154,123,184,0.34)',
  },
];
